/**
 * RWBR — CHOOSING THE PICTURE.
 *
 * NARRATION_MODEL §5 specifies selection as `momentTag × trackCategory ×
 * intensity`, with **no repeat inside a race** and **recency weighting across a
 * season**. This implements that.
 *
 * ── THE RULE THAT MATTERS MOST ─────────────────────────────────────────────
 *
 * **Never show a picture that contradicts the ledger.** The engine is the truth
 * and the photograph is decoration; a wet photo under a dry race, or a wreck
 * photo on a clean pass, does more damage to the fiction than showing no photo
 * at all. So filtering is strict and the fallback is *nothing*, never
 * "something roughly similar".
 *
 * That is why `pickImage` returns `null` freely. An empty panel is honest. A
 * wrong panel is a lie the player can see.
 *
 * ── WHY REPEATS ARE THE REAL ENEMY ─────────────────────────────────────────
 *
 * Measured: `challenge.sequence` fires **21.75 times per race**. With three
 * `pass` photos in the library the same image appears roughly seven times in one
 * race, which reads as broken rather than sparse. The no-repeat rule is
 * therefore not a nicety — it is the difference between a library that feels
 * real and one that feels like three pictures.
 *
 * When every candidate has already been used this race, the selector prefers
 * the **least recently used** rather than giving up, because a repeat separated
 * by forty turns is invisible and a repeat separated by two is glaring.
 */

import type { ArtImage, Condition, Intensity, Rarity, SceneTag, TrackCategory } from './scenes.ts';
import { RARITY, scenesForMoment } from './scenes.ts';

/**
 * HOW BIG A MOMENT IS, 0-100.
 *
 * This is the scale the rarity floors are set against, and it is the same
 * ordering a broadcast would cut on: the big one beats a duel beats a box swap.
 *
 * It lives in the domain rather than in the panel because two separate things
 * depend on it — which picture is cut to, and which *tier* of picture is even
 * eligible — and a second copy of this table drifting from the first would be
 * invisible until a legendary photo started firing on a routine pass.
 */
const SIGNIFICANCE: { test: (tag: string) => boolean; weight: number }[] = [
  // A race decided at the stripe is as big as this game gets — the only other
  // 100 is the Big One. `pfa.passed` (a pass for 14th) sits far below it, which
  // is the entire reason the two carry different tags.
  { test: (t) => t === 'pfa.won-the-race', weight: 100 },
  { test: (t) => t.startsWith('wreck.'), weight: 100 },
  { test: (t) => t === 'trouble.multi-car' || t === 'trouble.collected', weight: 90 },
  { test: (t) => t === 'trouble.caution', weight: 85 },
  { test: (t) => t.startsWith('rare.'), weight: 80 },
  { test: (t) => t.startsWith('feud.') || t.startsWith('fume.'), weight: 75 },
  { test: (t) => t === 'pit.violation' || t === 'problem.pit', weight: 70 },
  { test: (t) => t === 'pit.lead-change' || t === 'chip.laps-led' || t === 'pfa.passed', weight: 65 },
  { test: (t) => t === 'season.result' || t === 'raceweek.placement.pole', weight: 60 },
  { test: (t) => t.startsWith('problem.'), weight: 55 },
  { test: (t) => t === 'duel.win' || t === 'duel.chip-win' || t === 'duel.loss' || t === 'pfa.defended', weight: 50 },
  { test: (t) => t === 'pit.fast-pit', weight: 45 },
  // A trait chip changing hands is bookkeeping with a picture attached. It is
  // worth showing once, and it is worth showing less than anything that moved a
  // car — which is what a weight this low buys.
  { test: (t) => t === 'chip.trait-award', weight: 28 },
  { test: (t) => t === 'chip.trait-restored' || t === 'chip.trait-held', weight: 18 },
  { test: (t) => t === 'chip.reserve-activated', weight: 16 },
  // A restart event is a real incident, but it happens before the turn proper
  // — it sits with the yellow rather than with a wreck.
  { test: (t) => t === 'restart.trouble' || t === 'restart.turn-cancelled', weight: 70 },
  // A restart duel is a duel, and is weighted like one.
  { test: (t) => t.startsWith('restart-duel.'), weight: 50 },
  { test: (t) => t.startsWith('restart.'), weight: 42 },
  // ── The Bottom Group Churn (EZRA-S2 §3.12) ───────────────────────────────
  // A caution nobody has waved yet is a real incident, and is weighted as one.
  { test: (t) => t === 'churn.yellow-deferred', weight: 70 },
  // A Churn duel is a duel, and is weighted like one.
  { test: (t) => t.startsWith('churn-duel.'), weight: 50 },
  // The Porch is the only moment in a Churn with anything at stake beyond the
  // running order of cars nobody is watching — a chip, and a look at the
  // middle group. Worth more than the churning, less than a duel.
  { test: (t) => t === 'churn.porch', weight: 38 },
  // Positions changing hands twenty places back: just under the challenge
  // sequence it resembles, because it is the same thing happening where it
  // matters least.
  { test: (t) => t === 'churn.challenges', weight: 28 },
  // A Churn that started and did nothing is worth less than a shot of the
  // crowd, and this weight is what keeps it off the rarer tiers entirely.
  { test: (t) => t === 'churn.aborted' || t === 'churn.ignored', weight: 8 },
  { test: (t) => t === 'turn.yellow', weight: 40 },
  { test: (t) => t.startsWith('draft.'), weight: 35 },
  { test: (t) => t === 'challenge.sequence', weight: 30 },
  { test: (t) => t.startsWith('cam.'), weight: 25 },
];

export function momentSignificance(momentTag: string): number {
  for (const s of SIGNIFICANCE) if (s.test(momentTag)) return s.weight;
  return 10;
}

export function rarityOf(img: ArtImage): Rarity {
  return img.rarity ?? 'common';
}

export interface ArtContext {
  momentTag: string;
  trackCategory?: TrackCategory | string | null;
  intensity?: Intensity | null;
  condition?: Condition | null;
  /** Turn number, used only to make "least recently used" meaningful. */
  turn?: number;
  /**
   * How big this moment is, 0-100. Defaults to `momentSignificance(momentTag)`;
   * override only when the caller knows better than the tag does.
   */
  significance?: number;
}

export interface ArtPick {
  image: ArtImage;
  /** The tier that was actually served — `common` when the row is untiered. */
  rarity: Rarity;
  /** The scene that matched — useful in the ledger and for debugging. */
  scene: SceneTag;
  /** Higher is a better fit. Exposed so the UI can show near-misses in dev. */
  score: number;
  /** True when everything was already used and we re-showed the oldest. */
  reused: boolean;
}

/**
 * Remembers what has been shown. One per race; `noteSeason` carries a longer
 * memory across a career so the same photo is not the face of every wreck all
 * year.
 */
export class ArtHistory {
  /** imageId → the turn it was last shown, this race. */
  private thisRace = new Map<string, number>();
  /** imageId → how many times it has been shown, this race. */
  private raceCounts = new Map<string, number>();
  /** imageId → how many times it has been shown, all season. */
  private season = new Map<string, number>();

  /**
   * Clear the in-race memory. The SEASON memory deliberately survives, which is
   * what makes `maxPerSeason` mean anything — carry one ArtHistory across a
   * whole season and a legendary photo stays legendary.
   */
  startRace(): void {
    this.thisRace.clear();
    this.raceCounts.clear();
  }

  note(imageId: string, turn: number): void {
    this.thisRace.set(imageId, turn);
    this.raceCounts.set(imageId, (this.raceCounts.get(imageId) ?? 0) + 1);
    this.season.set(imageId, (this.season.get(imageId) ?? 0) + 1);
  }

  raceCount(imageId: string): number {
    return this.raceCounts.get(imageId) ?? 0;
  }

  usedThisRace(imageId: string): boolean {
    return this.thisRace.has(imageId);
  }

  lastShownTurn(imageId: string): number | null {
    return this.thisRace.get(imageId) ?? null;
  }

  seasonCount(imageId: string): number {
    return this.season.get(imageId) ?? 0;
  }
}

/**
 * WHAT CONDITIONS IS THIS RACE RUN IN?
 *
 * `App.tsx` used to hardcode `null` here, and `null` is not "no opinion" — it
 * is "unknown", and `scoreImage` lets a photo through when the context has no
 * condition to check it against. So the day somebody tagged a photo `wet`, a
 * wet picture could have surfaced under a dry race. Nothing was wrong on screen
 * because nothing was tagged yet, which is exactly the kind of stub that goes
 * bad silently.
 *
 * THE HONEST ANSWER IS `dry`, AND IT IS A POSITIVE CLAIM, NOT A DEFAULT.
 * Searched for and not found, in any sheet: rain, weather, a red flag for
 * conditions, a wet-track adjustment, a night-race rule, a time of day. The
 * base rulebook, the Race Week Events Guide, EZRA-S2, the Track Pack and the
 * Sagliano guide between them contain no weather rule of any kind, and the 31
 * track cards carry no weather or time-of-day column — only
 * `escapeAdj`/`repairAdj`, which are about the shape of the place. A race in
 * this game is therefore run in the dry, in daylight, always.
 *
 * It is a FUNCTION rather than a constant so the day a weather add-on turns up,
 * there is one place to teach and one place to grep. Deliberately NOT inferred
 * from a race name: "Coca-Cola 600" is a night race in the real world and the
 * material never says so, and guessing it here would be inventing a result.
 */
export const CITE_CONDITION =
  'No RWBR, RWEG, EZRA-S2, Track Pack or Sagliano sheet contains a weather or time-of-day rule, ' +
  'and no track card carries a weather column. Every race is run dry, in daylight.';

export function conditionForRace(_trackId?: string, _raceName?: string): Condition {
  return 'dry';
}

/**
 * Score one image against one context. Returns `null` when the image is
 * disqualified outright rather than merely a poor fit.
 *
 * DISQUALIFYING (a wrong picture is worse than none):
 *   - the scene does not match the moment at all
 *   - the photo is pinned to track categories and this is not one of them
 *   - the photo is pinned to conditions and this is not one of them
 *
 * SCORING (all else equal, prefer):
 *   - the scene the moment leads with, over its fallbacks
 *   - a matching intensity
 *   - a photo pinned to this track category over a general one
 *   - a photo shown less often this season
 */
export function scoreImage(img: ArtImage, ctx: ArtContext, history?: ArtHistory): ArtPick | null {
  const wanted = scenesForMoment(ctx.momentTag);
  if (wanted.length === 0) return null;

  // ── RARITY GATES, before anything else ─────────────────────────────────
  // A rare photo is not "unlikely" here, it is INELIGIBLE. The floor is what
  // makes rarity mean "saved for a bigger moment" rather than "shows at random
  // and usually on nothing".
  const tier = RARITY[rarityOf(img)];
  const significance = ctx.significance ?? momentSignificance(ctx.momentTag);
  if (significance < tier.minSignificance) return null;
  if (history) {
    if (history.raceCount(img.id) >= tier.maxPerRace) return null;
    if (history.seasonCount(img.id) >= tier.maxPerSeason) return null;
  }

  let bestScene: SceneTag | null = null;
  let bestRank = Infinity;
  for (const s of img.scenes) {
    const rank = wanted.indexOf(s);
    if (rank !== -1 && rank < bestRank) {
      bestRank = rank;
      bestScene = s;
    }
  }
  if (!bestScene) return null;

  // Pinned track categories are a hard filter, not a preference: a road-course
  // photo under a superspeedway race is visibly wrong.
  if (img.trackCategories.length > 0 && ctx.trackCategory) {
    if (!img.trackCategories.includes(ctx.trackCategory as TrackCategory)) return null;
  }
  if (img.conditions.length > 0 && ctx.condition) {
    if (!img.conditions.includes(ctx.condition)) return null;
  }

  // Leading scene beats fallback scene, decisively.
  let score = 100 - bestRank * 25;

  if (ctx.intensity && img.intensity) {
    score += img.intensity === ctx.intensity ? 12 : -8;
  }
  // A photo that specifically claims this track type is a better fit than a
  // generic one, given both are otherwise equal.
  if (ctx.trackCategory && img.trackCategories.includes(ctx.trackCategory as TrackCategory)) {
    score += 10;
  }
  // Spread the load across the season.
  score -= Math.min(15, (history?.seasonCount(img.id) ?? 0) * 1.5);

  return { image: img, scene: bestScene, score, reused: false, rarity: rarityOf(img) };
}

/**
 * Pick the picture for a moment, or `null` if nothing honest fits.
 *
 * `rng` is injected so selection is deterministic under the race seed — the
 * same race replayed shows the same pictures, which matters for the ledger
 * scrubbing in UI-001.
 */
export function pickImage(
  library: readonly ArtImage[],
  ctx: ArtContext,
  history?: ArtHistory,
  rng: () => number = Math.random,
): ArtPick | null {
  const scored: ArtPick[] = [];
  for (const img of library) {
    const p = scoreImage(img, ctx, history);
    if (p) scored.push(p);
  }
  if (scored.length === 0) return null;

  const turn = ctx.turn ?? 0;
  const fresh = history ? scored.filter((p) => !history.usedThisRace(p.image.id)) : scored;

  const pool = fresh.length > 0 ? fresh : scored;
  const reused = fresh.length === 0;

  if (reused && history) {
    // Everything has been seen this race. Show whatever has been off screen
    // longest — a repeat forty turns apart is invisible; two turns apart is not.
    let oldest = pool[0];
    let oldestTurn = history.lastShownTurn(oldest.image.id) ?? -Infinity;
    for (const p of pool) {
      const t = history.lastShownTurn(p.image.id) ?? -Infinity;
      if (t < oldestTurn) {
        oldest = p;
        oldestTurn = t;
      }
    }
    history.note(oldest.image.id, turn);
    return { ...oldest, reused: true };
  }

  // Take the best-matching band, then choose within it by RARITY WEIGHT rather
  // than uniformly. Score stays a hard ordering for match QUALITY — a leading
  // scene still beats a fallback, a track-pinned photo still beats a general
  // one — and rarity only decides between photos that fit equally well. Doing
  // it the other way round would let a legendary photo outrank a better-matched
  // common one, which is the tail wagging the dog.
  const best = Math.max(...pool.map((p) => p.score));
  const top = pool.filter((p) => p.score === best);
  const chosen = weightedPick(top, rng);

  history?.note(chosen.image.id, turn);
  return { ...chosen, reused: false };
}

/** Weighted random over the rarity weights, with a uniform fallback. */
function weightedPick(pool: ArtPick[], rng: () => number): ArtPick {
  const weights = pool.map((p) => RARITY[p.rarity].weight);
  const total = weights.reduce((a, b) => a + b, 0);
  if (!(total > 0)) return pool[Math.floor(rng() * pool.length) % pool.length];
  let r = rng() * total;
  for (let i = 0; i < pool.length; i++) {
    r -= weights[i];
    if (r <= 0) return pool[i];
  }
  return pool[pool.length - 1];
}

/**
 * How well a library actually covers a real stream of moments.
 *
 * Built to be run over an actual season rather than reasoned about, because
 * "we have 60 photos" says nothing about whether the *common* moments are
 * covered — and the demand is wildly uneven.
 */
export interface CoverageReport {
  moments: number;
  filled: number;
  empty: number;
  reused: number;
  /** momentTags that never found a picture, most frequent first. */
  gaps: { momentTag: string; count: number }[];
  /** How many distinct images actually got used. */
  distinctUsed: number;
}

export function measureCoverage(
  library: readonly ArtImage[],
  moments: readonly ArtContext[],
  rng: () => number = Math.random,
): CoverageReport {
  const history = new ArtHistory();
  history.startRace();
  const gaps: Record<string, number> = {};
  const used = new Set<string>();
  let filled = 0;
  let reused = 0;

  for (const m of moments) {
    const pick = pickImage(library, m, history, rng);
    if (!pick) {
      gaps[m.momentTag] = (gaps[m.momentTag] ?? 0) + 1;
      continue;
    }
    filled++;
    if (pick.reused) reused++;
    used.add(pick.image.id);
  }

  return {
    moments: moments.length,
    filled,
    empty: moments.length - filled,
    reused,
    distinctUsed: used.size,
    gaps: Object.entries(gaps)
      .map(([momentTag, count]) => ({ momentTag, count }))
      .sort((a, b) => b.count - a.count),
  };
}
